import { GameState } from './GameState';
import { EventBus, GameEvents } from '../core/EventBus';
import { eventApplyEffects } from './PlotSystem';
import { GameConfig } from '../core/GameConfig';
import { GameData } from '../data/GameData';
import { Utils } from '../core/Utils';
import { EventDef, ChoiceDef, AttributeKey, ScheduleSlot } from '../data/types';
import { gatesPass } from './GateSystem';

/**
 * 事件系统
 * 管理随机/固定事件的触发、D6判定、选项执行
 */
export class EventSystem {
    private static instance: EventSystem;

    private constructor() { }

    static getInstance(): EventSystem {
        if (!this.instance) {
            this.instance = new EventSystem();
        }
        return this.instance;
    }

    /** 触发随机事件（每周行程后调用，slot 为当周真实执行的行程） */
    triggerRandomEvent(slot: ScheduleSlot | null): EventDef | null {
        const event = this.rollRandomEvent(slot);
        if (event) this.triggerEvent(event);
        return event;
    }

    /**
     * 仅掷出随机事件候选（不触发 EVENT_TRIGGERED，不应用任何效果）
     * 供月度执行弹窗内联展示：命中返回事件供 UI 呈现选项，玩家选择后再 executeChoice
     * 触发条件：当周行程匹配事件 triggerSchedule（不写 = 任意行程；'SCRIPT_*' 前缀通配）
     *          + 通过 gates 状态闸门（第 45 批，见 GateSystem）+ 通过 triggerProbability 概率
     */
    rollRandomEvent(executedSlot: ScheduleSlot | null): EventDef | null {
        const pool = GameData.getRandomEvents();
        const slotRefId = executedSlot?.refId ?? null;
        const player = GameState.getInstance().player;

        // 过滤出满足触发条件的（行程匹配检查 + 状态闸门 + 概率判定）
        const candidates = pool.filter(event => {
            // 行程匹配：triggerSchedule 不写/空数组 = 任意行程；有值则需包含当周行程 refId（'*' 结尾按前缀通配）
            const scheduleIds = event.triggerSchedule ?? [];
            if (scheduleIds.length > 0 && (slotRefId === null || !scheduleIds.some(id =>
                id.endsWith('*') ? slotRefId.startsWith(id.slice(0, -1)) : id === slotRefId))) {
                return false;
            }

            // 状态闸门（45 批）：咖位/属性/金钱等不达标直接出局，不占概率
            if (!gatesPass(event.gates, player)) return false;

            // 概率判定
            const prob = event.triggerProbability ?? 0;
            return Math.random() < prob;
        });

        if (candidates.length === 0) return null;

        // 随机选一个
        return candidates[Math.floor(Math.random() * candidates.length)];
    }

    /** 主动触发任意事件（用于外部调用） */
    triggerEventById(eventId: string): boolean {
        const event = GameData.EVENT_MAP[eventId];
        if (!event) return false;
        this.triggerEvent(event);
        return true;
    }

    /** 执行事件（触发事件总线通知） */
    private triggerEvent(event: EventDef): void {
        EventBus.emit(GameEvents.EVENT_TRIGGERED, event);
        console.log(`[EventSystem] 触发事件: ${event.title}`);
    }

    /**
     * 执行选项判定
     * 三类选项互斥：
     *   - direct（默认开启，必生效）：直接应用 effects，返回 success:true，不掷骰
     *   - tag（拥有标签才开启，必生效）：getAvailableChoices 已过滤保证可见，直接应用 effects
     *   - check（绑一种属性判定）：D6 判定成功应用 effects，失败不应用
     * 返回 { success, roll, total }；direct/tag 无判定概念，roll/total 为 0
     */
    executeChoice(event: EventDef, choice: ChoiceDef): { success: boolean; roll: number; total: number } {
        const state = GameState.getInstance();

        if (choice.type === 'check') {
            const bindAttr = choice.bindAttr;
            const attrValue = state.getAttribute(bindAttr);
            const dc = choice.dc;
            const roll = Utils.rollD6();
            // 锦鲤附体（TAG_015）：D6 判定 +1（骰权随 ATTR_SCALE 缩放，与 Utils 同式）
            const lucky = state.hasTag('TAG_015') ? 1 : 0;
            // 批3-B：属性折半入判定；批3-A：骰权随 ATTR_SCALE 缩放（与 Utils.checkSkill/successProbability 同式）
            const total = Math.floor(attrValue / 2) + (roll + lucky) * GameConfig.DICE_FACE_WEIGHT;
            const success = total >= dc;

            if (success) {
                // 成功：应用正面效果
                this.applyEffects(choice.effects);
            } else {
                // 失败：应用 failEffects（批3-B 加重档：单项属性 -3 / 资产百分比金钱 / 口碑 -2~-3）；
                // 无 failEffects 时维持失败无效果（纯错过机会）
                if (choice.failEffects) {
                    this.applyEffects(choice.failEffects);
                }
            }

            EventBus.emit(GameEvents.CHOICE_MADE, choice.id, success, roll, total);

            return { success, roll, total };
        }

        // direct / tag：必定生效，无判定
        this.applyEffects(choice.effects);

        EventBus.emit(GameEvents.CHOICE_MADE, choice.id, true, 0, 0);

        return { success: true, roll: 0, total: 0 };
    }

    /** 应用选项效果（批 2 起委托 PlotSystem 的公共实现——同一键表，随机事件与剧情事件共用） */
    private applyEffects(effects: Record<string, number>): void {
        eventApplyEffects(effects);
    }

    /** 获取事件的可用选项（过滤掉不满足标签要求的 tag 型选项） */
    getAvailableChoices(event: EventDef): ChoiceDef[] {
        const state = GameState.getInstance();
        return event.choices.filter(choice => {
            if (choice.type === 'tag' && !state.hasTag(choice.requiredTag)) {
                return false;
            }
            return true;
        });
    }

    /**
     * 获取事件的判定成功率（用于 UI 显示）
     * 规则：check 型选项显示绑定属性的判定通过率；
     * direct / tag 型选项返回 null → UI 显示「效果 · …」预览。
     */
    getSuccessProbability(event: EventDef, choice: ChoiceDef): number | null {
        if (choice.type !== 'check') return null;
        const state = GameState.getInstance();
        const attrValue = state.getAttribute(choice.bindAttr);
        return Utils.successProbability(attrValue, choice.dc);
    }
}

/** 导出单例 */
export const eventSystem = EventSystem.getInstance();